import {
	Breakpoints,
	BreakpointWrapper,
} from '@volkswagen-onehub/components-core';
import {
	CloseHandleV2,
	FocusLayerSizeV2,
	LayerManagerV2,
} from '@volkswagen-onehub/layer-manager';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
	isNull,
	LayerManagerWrapper,
	NewDealerCard,
	useFeatureAppConfig,
	useFeatureAppEnvironment,
	useMapGeneralController,
	weekDay_Day_Hour,
} from 'src/feature-app';
import { CalendarioCita } from 'src/feature-app/CalendarioCita/';
import {
	getDistanceFromLatLngCenter,
	LayerDesktopView,
	LayerMobileView,
} from 'src/feature-app/NewMap';
import { MapGeneralControllerContext } from 'src/feature-app/NewMap/MapGeneralController';
import {
	getEarliestSlot,
	sortPointsByDistance,
} from 'src/feature-app/NewMap/sort-points';
import { useOneFormContext } from 'src/feature-app/OneForm';
import { installationsIDBuzz, useIsComerciales } from 'src/feature-app/hooks';
import { useFeatureServices } from 'src/feature-app/hooks/use-feature-services';
import {
	fakeMotivoValues,
	ReservaCitaLayer,
} from 'src/forms/CitaPosventa/NuevaCitaPosventa';
import {
	Availability,
	CalendarSlot,
	DealersData,
	Horario,
	OneFormState,
	Points,
	SelectedTab,
	Steps,
} from 'src/types';
import {
	AvailabilityHistory,
	createAvailabilityManager,
	createHideCalendarHandler,
	createShowCalendarHandler,
	handleDmsMapExit as helperHandleDmsMapExit,
	handleNoDmsMapExit as helperHandleNoDmsMapExit,
} from './helpers/availabilityHelper';

export const JourneyCitaPosventaController = () => {
	const { formData, formInfo, dealersInfo } = useSelector(
		(state: OneFormState) => state,
	);
	const { horario, dealer }: { horario?: Horario; dealer?: DealersData } =
		formData.fields;
	const { formEnded, showCalendarOnLoad } = formInfo;
	const { points, selectedDealer } = dealersInfo;
	const { handleNextStep, handleScreenChange, setIsFullScreen } =
		useOneFormContext();
	const layerManager = useFeatureServices()[
		'layer-manager'
	] as LayerManagerV2;
	const store = useStore();
	const dispatch = useDispatch();
	const env = useFeatureAppEnvironment();
	const config = useFeatureAppConfig();
	const [showCalendar, setShowCalendar] = useState<boolean>(false);
	const [error, setError] = useState(false);
	const [preSelectedOrderValue, setPreSelectedOrderValue] =
		useState<SelectedTab>('ubicacion');
	const pointsByTimeRef = useRef(null);
	const pointsByDistanceRef = useRef(null);
	const isComerciales = useIsComerciales();

	const [firstAvailability, setFirstAvailability] =
		useState<Availability>(null);
	const availabilityHistory = useRef<AvailabilityHistory[]>([]);
	const loadingLayer = useRef(null);
	const {
		clusters,
		centerToCalculateDistance,
		superClusterRef,
		cardsDistanceRef,
		cardsTimeRef,
		selectedPoint,
		handleSelectedPoint,
	} = useMapGeneralController();

	const [pointsToList, setPointsToList] = useState(null);
	const [selectedSlot, setSelectedSlot] = useState(null);

	const filterClusters = useCallback((clusters: Points[]) => {
		const { formData } = store.getState();

		// Filtro si se selecciona instalacion placa The Originals
		if (formData.fields?.motivo === fakeMotivoValues.placaOriginals) {
			return clusters.filter((point: Points) => {
				return !installationsIDBuzz.includes(
					point.properties?.dealer?.codInstalacion,
				);
			});
		}
		return clusters;
	}, []);

	useEffect(() => {
		if (
			(formEnded && (dealer || selectedDealer) && showCalendarOnLoad) ||
			showCalendarOnLoad
		) {
			const point = points.find(
				(point: Points) =>
					point.properties.dealer.kvps === dealer?.kvps ||
					point.properties.dealer.kvps === selectedDealer?.kvps,
			);
			handleShowCalendarOnClick(point);
		}
	}, []);

	//
	// Este useEffect sustituye a los anteriores.
	//
	useEffect(() => {
		if (clusters && superClusterRef.current) {
			const filteredClusters = filterClusters(clusters);
			const pointsByDistance = sortPointsByDistance(
				filteredClusters,
				centerToCalculateDistance.current,
				superClusterRef.current,
			);
			pointsByDistanceRef.current = pointsByDistance;
			setPointsToList(pointsByDistance);
		}
	}, [clusters, superClusterRef.current]);

	const availabilityManager = createAvailabilityManager(
		layerManager,
		store,
		env,
		setFirstAvailability,
		setError,
		availabilityHistory,
		loadingLayer,
	);

	const { getAvailability } = availabilityManager;

	const handleDmsMapExit = (
		slot: CalendarSlot | CalendarSlot[],
		point: Points,
	) => {
		helperHandleDmsMapExit(
			slot,
			point,
			availabilityHistory.current,
			payload => {
				handleDealerFlags(payload.dealer);

				dispatch({
					type: 'UPDATE_FIELDS',
					payload,
				});

				if (!formEnded) {
					handleNextStep();
				}
			},
		);
	};

	const handleNoDmsMapExit = (
		franjaHoraria: string,
		day: string,
		point: Points,
	) => {
		helperHandleNoDmsMapExit(
			franjaHoraria,
			day,
			point,
			payload => {
				handleDealerFlags(payload.dealer);

				dispatch({
					type: 'UPDATE_FIELDS',
					payload,
				});

				if (!formEnded) {
					handleNextStep();
				}
			},
			availabilityHistory.current,
		);
	};

	const handleShowSlots = useCallback((point: Points): string | null => {
		const { slots } = point.properties?.dealer;
		let finalSlot;

		if (!isNull(slots)) {
			finalSlot = getFinalSlot(getEarliestSlot(slots));

			return finalSlot ? finalSlot : null;
		}

		return null;
	}, []);

	const getFinalSlot = (from: number) => {
		if (!from) {
			return null;
		}
		const momentSlot = weekDay_Day_Hour(from);
		const capitalizedSlot =
			momentSlot.charAt(0).toUpperCase() + momentSlot.slice(1);
		return capitalizedSlot.replace('-', ' a las ').concat('h');
	};

	const handleShowCalendarOnClick = createShowCalendarHandler(
		getAvailability,
		handleSelectedPoint,
		setShowCalendar,
	);
	const handleHideCalendarOnClick = createHideCalendarHandler(
		handleSelectedPoint,
		setShowCalendar,
	);

	const handleDealerFlags = (dealer: DealersData) => {
		const { formInfo, formData } = store.getState();
		const { steps } = formInfo;
		const {
			initialSteps,
			formEnded,
		}: { initialSteps: Steps[]; formEnded: boolean } = formInfo;
		const {
			cocheSustitucion,
			cocheAlTaller,
			dealer: pastDealer,
		}: {
			cocheSustitucion: 'true' | 'false';
			cocheAlTaller: 'true' | 'false';
			dealer: DealersData;
		} = formData.fields;
		const newSteps = [...steps];

		if (isComerciales) {
			return;
		}

		if (
			!dealer ||
			!dealer.dmsInfo ||
			!dealer.dmsInfo.pickupCarServiceAvailabe
		) {
			const tallerIndex = newSteps.findIndex(
				step => step.name === 'CocheAlTaller',
			);
			newSteps.splice(tallerIndex, 1);
		}

		if (
			!dealer ||
			!dealer.dmsInfo ||
			!dealer.dmsInfo.replacementCarServiceAvailable
		) {
			const sustitucionIndex = newSteps.findIndex(
				step => step.name === 'CocheSustitucion',
			);
			newSteps.splice(sustitucionIndex, 1);
		}

		const newStepsWithIndex = newSteps.map((step, index) => {
			step.screenIndex = index;
			return step;
		});

		// Pasamos de dealer sin dms a uno con dms después de llegar a la pantalla de confirmación y volvemos a atrás con editar.
		let newIndex: number;
		let newCocheAlTaller = cocheAlTaller;
		let newCocheSustitucion = cocheSustitucion;
		if (formEnded) {
			// dispatch({ type: 'UPDATE_FIELDS', payload: { cocheSustitucion: null, cocheAlTaller: null } });
			dispatch({ type: 'UPDATE_FORM_ENDED', payload: false });

			if (pastDealer.kvps !== dealer.kvps) {
				if (dealer.dmsInfo.pickupCarServiceAvailabe) {
					if (
						cocheAlTaller === 'false' &&
						pastDealer &&
						pastDealer.dmsInfo &&
						!pastDealer.dmsInfo.pickupCarServiceAvailabe
					) {
						newIndex = newStepsWithIndex.findIndex(
							step => step.name === 'CocheAlTaller',
						);
						newCocheAlTaller = null;
					}
				}
				if (dealer.dmsInfo.replacementCarServiceAvailable) {
					if (
						cocheAlTaller === 'false' &&
						pastDealer &&
						pastDealer.dmsInfo &&
						!pastDealer.dmsInfo.replacementCarServiceAvailable
					) {
						newIndex = newStepsWithIndex.findIndex(
							step => step.name === 'CocheSustitucion',
						);
						newCocheSustitucion = null;
					}
				}
			}
		}

		dispatch({ type: 'UPDATE_STEPS', payload: newStepsWithIndex });
		dispatch({
			type: 'UPDATE_LAST_STEP',
			payload: newStepsWithIndex[newStepsWithIndex.length - 1],
		});
		dispatch({
			type: 'SET_NUMBER_OF_SCREENS',
			payload: newStepsWithIndex.length,
		});
		// Solo se llega a esta parte después de finalizar el journey y volver al mapa para editar la concesión.
		if (formEnded) {
			newIndex = newIndex ? newIndex : newStepsWithIndex.length - 1;
			dispatch({
				type: 'UPDATE_FIELDS',
				payload: {
					cocheSustitucion: newCocheSustitucion,
					cocheAlTaller: newCocheAlTaller,
				},
			});

			dispatch({
				type: 'UPDATE_STEP',
				payload: newStepsWithIndex[newIndex],
			});
			handleScreenChange(newIndex);
			if (newStepsWithIndex[newIndex].name !== 'Confirmacion') {
				dispatch({ type: 'UPDATE_FULLSCREEN', payload: false });
				setIsFullScreen(false);
			}
		}
	};

	const renderCalendar = (): JSX.Element => {
		const { centerToCalculateDistance, userIsGeoLocated } =
			useMapGeneralController();

		return error ? null : (
			<CalendarioCita
				selectedPoint={selectedPoint}
				paramAvailability={firstAvailability}
				getAvailability={getAvailability}
				selectedSlot={selectedSlot}
				setSelectedSlot={setSelectedSlot} // Comprobar
				handleDmsMapExit={handleDmsMapExit}
				handleNoDmsMapExit={handleNoDmsMapExit}
				center={centerToCalculateDistance.current}
				horario={horario}
				handleHideCalendarOnClick={handleHideCalendarOnClick}
				userIsGeoLocated={userIsGeoLocated}
			/>
		);
	};

	const renderPoints = (
		points: Points[],
		carPickupSelected?: boolean,
		replacementCarSelected?: boolean,
	): JSX.Element[] => {
		const filteredClusters = filterClusters(pointsByDistanceRef.current);

		return pointsByDistanceRef.current
			? filteredClusters.map((point: Points, index: number) => {
					if (!point.properties.cluster) {
						const { dealer } = point.properties;
						const distanceFromCenter = getDistanceFromLatLngCenter(
							centerToCalculateDistance.current,
							point,
						);

						return (
							<div
								key={`${point.properties.dealer.kvps}-${index}`}
								className={`${point.properties.dealer.kvps}`}
								ref={el => {
									cardsDistanceRef.current[index] = el;
								}}
							>
								<NewDealerCard
									handleShowSlots={handleShowSlots}
									key={`${point.properties.dealer.kvps}-${index}-card`}
									distanceFromCenter={distanceFromCenter}
									handleShowCalendarOnClick={
										handleShowCalendarOnClick
									}
									handleSeleccionarOnClick={
										handleSeleccionarOnClick
									}
									isCarousel={false}
									slots={dealer.slots}
									dealerInfo={dealer}
									point={point}
									isCitaPosventa={true}
									isFirstDesktop={index === 0 ? true : false}
									carPickupSelected={carPickupSelected}
									replacementCarSelected={
										replacementCarSelected
									}
								/>
							</div>
						);
					}
					return null;
			  })
			: null;
	};

	// Gestión del point seleccionado y del boton de seleccionar (dms)
	const handleSeleccionarOnClick = useCallback((point: any) => {
		handleSelectedPoint(point);
		handleReservaCitaLayer(point);
	}, []);

	const renderReservaCita = (state: any, close: CloseHandleV2<any, any>) => {
		return (
			<LayerManagerWrapper store={store} env={env}>
				<ReservaCitaLayer
					handleShowCalendarOnClick={handleShowCalendarOnClick}
					handleDmsMapExit={handleDmsMapExit}
					handleNoDmsMapExit={handleNoDmsMapExit}
					handleShowSlots={handleShowSlots}
					selectedPoint={state.selectedPoint}
					getAvailability={getAvailability}
					closeLayerCallback={close}
				/>
			</LayerManagerWrapper>
		);
	};

	const handleReservaCitaLayer = (point: Points) => {
		layerManager.openFocusLayer(
			renderReservaCita,
			{ selectedPoint: point },
			{ size: FocusLayerSizeV2.A },
		);
	};

	const mapController = useMapGeneralController();

	// Crear un contexto personalizado con clusters filtrados
	const filteredMapController = useMemo(
		() => ({
			...mapController,
			clusters: filterClusters(clusters),
		}),
		[mapController, clusters, filterClusters],
	);

	return (
		<MapGeneralControllerContext.Provider value={filteredMapController}>
			<div className="cita-posventa-map-controller">
				<>
					<BreakpointWrapper max={Breakpoints.b960}>
						<LayerMobileView
							handleSeleccionarOnClick={handleSeleccionarOnClick}
							handleShowSlots={handleShowSlots}
							renderPoints={renderPoints}
							handleShowCalendarOnClick={
								handleShowCalendarOnClick
							}
							renderCalendar={renderCalendar}
							showCalendar={showCalendar}
							preSelectedOrderValue={preSelectedOrderValue}
							setPreSelectedOrderValue={setPreSelectedOrderValue}
							pointsToList={pointsToList}
							isPosventa={true}
							services={true}
							paddingTopList={isComerciales ? '56px' : null}
						/>
					</BreakpointWrapper>

					<BreakpointWrapper min={Breakpoints.b960}>
						<LayerDesktopView
							handleSeleccionarOnClick={handleSeleccionarOnClick}
							handleShowSlots={handleShowSlots}
							renderPoints={renderPoints}
							renderCalendar={renderCalendar}
							showCalendar={showCalendar}
							preSelectedOrderValue={preSelectedOrderValue}
							setPreSelectedOrderValue={setPreSelectedOrderValue}
							pointsToList={pointsToList}
							isPosventa={true}
							services={true}
							paddingBottomList={isComerciales ? '32px' : null}
						/>
					</BreakpointWrapper>
				</>
			</div>
		</MapGeneralControllerContext.Provider>
	);
};
